/*!
 * ShockSims — Fitment Attribution v1.2
 * ---------------------------------------------------------------------------
 * Captures the YMM selection from shocksims-ymm-finder and the VIN decode from
 * shocksims-widget, then attaches them to the Shopify order as cart attributes,
 * which land once per order under "Additional details" in admin.
 *
 * Line item properties are available but OFF by default — see TAG_LINE_ITEMS.
 *
 * INSTALL
 *   1. Assets > Add a new asset > ss-fitment.js  (paste this file)
 *   2. layout/theme.liquid, before </head>, ABOVE the shocksims-fitment-assets
 *      script tag:   {{ 'ss-fitment.js' | asset_url | script_tag }}
 *   3. Apply the two patches in ss-fitment-patches.md
 *
 * API
 *   SSFitment.setYMM({ year, make, model, trim, engine, recommends })
 *   SSFitment.setVIN(vin, { vehicle, year, trim, engine, rpo, recommends, outcome })
 *   SSFitment.get() / .clear() / .refresh() / .properties()
 *   SSFitment.applyToExistingCart()
 *
 * `recommends` accepts a product handle, a /products/ URL, or a display name.
 * Handles are resolved through PRODUCT_NAMES below.
 * ---------------------------------------------------------------------------
 */
(function (window, document) {
  'use strict';

  if (window.SSFitment) return;   // re-entry guard, matching the widget's pattern

  var STORAGE_KEY = 'ss_fitment';
  var TTL_DAYS    = 30;      // stale selections stop riding along after this
  var DEBUG       = false;   // flip to true while testing, then back to false

  /* false = the fitment lives only in "Additional details" on the order, once
     per order, and no line item carries anything.

     The one thing this gives up: "Buy it now" builds a checkout straight from
     the product form and never touches the cart, so cart attributes do not
     reach those orders. If your product pages show dynamic checkout buttons
     (Shop Pay, PayPal, "Buy it now"), those orders will record no fitment at
     all. Either leave those buttons off, or set this back to true. */
  var TAG_LINE_ITEMS = false;

  /* The capture runs in the CUSTOMER's browser, so the raw timestamp is in
     their timezone. This pins the recorded time to the store's clock, which is
     the one on the order header you will be comparing it against. DST is
     handled automatically — August prints EDT, January prints EST. */
  var STORE_TZ = 'America/New_York';

  /* Handle -> display name, so the order page reads "OBD/SS ARC" rather than
     "obd-ss-arc-adaptive-ride-control-bypass". Mirrors PRODUCTS in
     shocksims-widget.js. An unlisted handle falls through unchanged. */
  var PRODUCT_NAMES = {
    'obd-ss-classic-magneride-bypass':        'OBD/SS Classic',
    'obd-ss-carbon-magneride-bypass':         'OBD/SS Carbon',
    'obd-ss-elite-gm-suv-suspension-bypass':  'OBD/SS Elite',
    'obd-ss-arc-adaptive-ride-control-bypass':'OBD/SS ARC',
    'arc-max-shock-sims-duramax':             'ARC-MAX',
    'vdm-e-ford-vehicle-dynamics-emulator':   'OBD/SS Ford VDM-E',
    'obd-ss-apex-gm-suv-suspension-bypass':   'OBD/SS Apex',
    'obd-ss-legacy-magneride-bypass':         'OBD/SS Legacy',
    'ride-height-shock-sim-kit':              'Ride Height & Shock Sim Kit',
    'obd-ii-y-splitter-cable':                'OBD-II Y-Splitter Cable'
  };

  /* Products that are not vehicle-specific. Their line items get NO fitment
     properties — the vehicle still reaches the order through the cart
     attributes in "Additional details". Handles, not titles: confirm each one
     against the product URL, or run SSFitment.audit() on the page.

     Anything not listed here is tagged, so a new module is never silently
     missed — only accessories need adding. */
  var SKIP_HANDLES = [
    'obd-ii-y-splitter-cable',
    'dust-cover-plugs-shock-connectors'
  ];

  /* rec.status from the widget's recommendation engine, in plain words. */
  var OUTCOMES = {
    ok:          'Product recommended',
    diy:         'Advice only, no module',
    no_module:   'No module needed',
    unsupported: 'Not supported',
    needs_vin:   'Could not confirm'
  };

  function log() {
    if (DEBUG && window.console && console.log) {
      console.log.apply(console, ['[SSFitment]'].concat([].slice.call(arguments)));
    }
  }

  /* ----------------------------------------------------------------- store */

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = name + '=' + value + ';expires=' + d.toUTCString() +
                      ';path=/;SameSite=Lax';
  }

  function getCookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? m.pop() : '';
  }

  function load() {
    var raw = '';
    try { raw = window.localStorage.getItem(STORAGE_KEY) || ''; } catch (e) {}
    if (!raw) { try { raw = decodeURIComponent(getCookie(STORAGE_KEY) || ''); } catch (e) {} }
    if (!raw) return null;

    var data = null;
    try { data = JSON.parse(raw); } catch (e) { return null; }
    if (!data || !data.ts) return null;
    if (Date.now() - data.ts > TTL_DAYS * 86400000) { clear(); return null; }
    return data;
  }

  function save(data) {
    var raw = JSON.stringify(data);
    try { window.localStorage.setItem(STORAGE_KEY, raw); } catch (e) {}
    setCookie(STORAGE_KEY, encodeURIComponent(raw), TTL_DAYS);
  }

  function clear() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    setCookie(STORAGE_KEY, '', -1);
    removeInputs();
  }

  /* ------------------------------------------------------------ formatting */

  function str(v) { return (v === null || v === undefined) ? '' : String(v).trim(); }

  function vehicleString(d) {
    if (d.vehicleRaw) return d.vehicleRaw;   // decoded title from the VIN service
    // Needs at least a make or a model to be a vehicle description. The Ford
    // path carries no title at all, and a partial VIN decode can return a trim
    // with no title — without this those become "_Vehicle: 2019" and
    // "_Vehicle: 2014 Premium", which are worse than an absent field.
    var make = str(d.make), model = str(d.model);
    if (!make && !model) return '';
    return [str(d.year), make, model, str(d.trim)].filter(Boolean).join(' ');
  }

  /* "August 31, 2026 at 9:33 am EDT" — store time, matching the format of the
     order header so the two can be read side by side. Drop timeZoneName below
     if you would rather not see the EDT/EST suffix. */
  function stamp(ts) {
    var d = new Date(ts);
    try {
      var p = new Intl.DateTimeFormat('en-US', {
        timeZone: STORE_TZ,
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZoneName: 'short'
      }).formatToParts(d).reduce(function (o, x) { o[x.type] = x.value; return o; }, {});

      return p.month + ' ' + p.day + ', ' + p.year + ' at ' +
             p.hour + ':' + p.minute + ' ' + String(p.dayPeriod).toLowerCase() +
             ' ' + p.timeZoneName;
    } catch (e) {
      return d.toISOString();   // ancient browser without Intl: better than nothing
    }
  }

  /* Accepts "OBD/SS ARC", "/products/obd-ss-arc-…", or a bare handle. */
  function productName(v) {
    v = str(v);
    if (!v) return '';
    var m = v.match(/\/products\/([a-z0-9\-_]+)/i);
    var handle = m ? m[1] : (/^[a-z0-9][a-z0-9\-]*$/.test(v) ? v : null);
    if (!handle) return v;
    return PRODUCT_NAMES[handle] || handle;
  }

  // Hidden (underscore-prefixed) line item properties: invisible to the
  // customer in cart and checkout, fully visible to you in the Shopify admin.
  function lineItemProperties(d) {
    var p = {};
    p['_Fitment Source'] = d.source;
    if (d.vehicle)    p['_Vehicle']             = d.vehicle;
    if (d.year)       p['_Vehicle Year']        = str(d.year);   // exact year
    if (d.make)       p['_Vehicle Make']        = str(d.make);
    if (d.model)      p['_Vehicle Model']       = str(d.model);
    if (d.trim)       p['_Vehicle Trim']        = str(d.trim);
    if (d.engine)     p['_Vehicle Engine']      = str(d.engine);
    if (d.vin)        p['_VIN']                 = d.vin;
    if (d.rpo)        p['_RPO Codes']           = str(d.rpo);
    if (d.recommends) p['_Fitment Recommended'] = d.recommends;
    if (d.outcome)    p['_Fitment Outcome']     = d.outcome;
    p['_Fitment Captured'] = stamp(d.ts);
    return p;
  }

  // Cart-level attributes: land under "Additional details" on the order page.
  // Shopify does not render these to the customer anywhere by default, and it
  // drops any attribute whose value is an empty string — so a field that has
  // nothing to say simply does not appear.
  function cartAttributes(d) {
    var vehicle = str(d.vehicle);
    var year    = str(d.year);
    var trim    = str(d.trim);

    /* Only state the year and trim when the vehicle line does not already
       carry them. On the normal path "2017 GMC Yukon XL" says the year itself.
       On a Ford VIN, which decodes locally with no title, there is no vehicle
       line at all — and then the year is the only record of it. */
    var yearSaid = year && vehicle.indexOf(year) === 0;
    var trimSaid = trim && vehicle.indexOf(trim) !== -1;

    return {
      'Fitment Tool':        d.source,
      'Fitment Vehicle':     vehicle,
      'Fitment Year':        yearSaid ? '' : year,
      'Fitment Trim':        trimSaid ? '' : trim,
      'Fitment Engine':      str(d.engine),
      'Fitment VIN':         d.vin || '',
      'Fitment RPO':         str(d.rpo),
      'Fitment Recommended': d.recommends || '',
      'Fitment Outcome':     d.outcome || '',
      'Fitment Captured':    stamp(d.ts)
    };
  }

  /* -------------------------------------------------- product form plumbing */
  /* Horizon, like every modern theme, serializes the product form and POSTs it
     to /cart/add.js — and "Buy it now" submits the same form. Injecting hidden
     inputs therefore covers add-to-cart, quick-add and dynamic checkout at once. */

  var MARK = 'data-ss-fitment';

  function forms() {
    return [].slice.call(document.querySelectorAll('form[action*="/cart/add"]'));
  }

  function removeInputs(scope) {
    [].slice.call((scope || document).querySelectorAll('input[' + MARK + ']'))
      .forEach(function (el) { el.parentNode && el.parentNode.removeChild(el); });
  }

  /* Best-effort product handle for a given add-to-cart form. Themes vary, so
     this cascades: an explicit data attribute, then a product link inside the
     surrounding card (quick-add tiles and upsell blocks), then the page URL —
     which is what a product page resolves to, and therefore what every
     "Buy it now" click resolves to, since that button only exists there.
     Returns null when nothing matches. */
  function formHandle(form) {
    if (!form.closest) return null;

    var host = form.closest('[data-product-handle]');
    if (host) return host.getAttribute('data-product-handle') || null;

    var scope = form.closest('li, article, product-card, .card, .card-wrapper, .quick-add, .product-card');
    if (scope) {
      var link = scope.querySelector('a[href*="/products/"]');
      var m = link && (link.getAttribute('href') || '').match(/\/products\/([a-z0-9][a-z0-9\-_]*)/i);
      if (m) return m[1];
    }

    var m2 = window.location.pathname.match(/\/products\/([a-z0-9][a-z0-9\-_]*)/i);
    return m2 ? m2[1] : null;
  }

  // Unknown handle => tag it. Only a listed accessory is skipped.
  function skipForm(form) {
    var h = formHandle(form);
    return !!h && SKIP_HANDLES.indexOf(h) !== -1;
  }

  function injectInputs() {
    if (!TAG_LINE_ITEMS) { removeInputs(); return; }   // attributes-only mode
    var d = load();
    if (!d) return;
    var props = lineItemProperties(d);
    var tagged = 0;

    forms().forEach(function (form) {
      if (skipForm(form)) { removeInputs(form); return; }   // accessory: leave it clean
      tagged++;
      Object.keys(props).forEach(function (key) {
        var name = 'properties[' + key + ']';
        var existing = form.querySelector('input[name="' + name + '"][' + MARK + ']');
        if (existing) { existing.value = props[key]; return; }
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = props[key];
        input.setAttribute(MARK, '');
        form.appendChild(input);
      });
    });
    log('tagged ' + tagged + ' of ' + forms().length + ' form(s)', props);
  }

  /* Shows what every add-to-cart form on the page resolves to and whether it
     will be tagged. Use it to confirm an accessory's handle before adding it to
     SKIP_HANDLES: SSFitment.audit() in the console. */
  function audit() {
    if (!TAG_LINE_ITEMS) {
      var msg = 'TAG_LINE_ITEMS is false — no line item is tagged. ' +
                'The fitment rides on the order as cart attributes only.';
      if (window.console) console.log('[SSFitment] ' + msg);
      return [];
    }
    var rows = forms().map(function (form, i) {
      var h = formHandle(form);
      return {
        form: i,
        handle: h || '(unresolved)',
        tagged: !skipForm(form),
        why: h ? (SKIP_HANDLES.indexOf(h) !== -1 ? 'in SKIP_HANDLES' : 'not an accessory')
               : 'handle unknown, tagged by default'
      };
    });
    if (window.console && console.table) console.table(rows);
    return rows;
  }

  // Themes re-render sections (quick-add drawers, variant swaps), so watch.
  function watchForms() {
    if (!window.MutationObserver) return;
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(injectInputs, 60);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* --------------------------------------------------------- cart mirroring */

  var syncing = false, pendingSync = false;

  function rootUrl() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function syncCartAttributes() {
    var d = load();
    if (!d) return;
    /* Queue rather than drop. Two captures in the same tick (a VIN decode
       immediately followed by a YMM pick) used to leave the cart holding only
       the first one's attributes. */
    if (syncing) { pendingSync = true; return; }
    syncing = true;
    fetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: cartAttributes(d) })
    })
      .then(function () { log('cart attributes synced'); })
      .catch(function (e) { log('attribute sync failed', e); })
      .then(function () {
        syncing = false;
        if (pendingSync) { pendingSync = false; syncCartAttributes(); }
      });
  }

  /* Retro-fit items already in the cart when a tool is used late in the session.
     Merges rather than replaces, since /cart/change.js overwrites the whole
     properties object. */
  function applyToExistingCart() {
    // Cart attributes apply to the whole cart whenever they are set, so there
    // is nothing to retro-fit unless line items are being tagged.
    if (!TAG_LINE_ITEMS) return Promise.resolve();
    var d = load();
    if (!d) return Promise.resolve();
    var props = lineItemProperties(d);

    return fetch(rootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var chain = Promise.resolve();
        (cart.items || []).forEach(function (item) {
          if (item.properties && item.properties['_Fitment Source']) return;  // already tagged
          var merged = {};
          Object.keys(item.properties || {}).forEach(function (k) { merged[k] = item.properties[k]; });
          Object.keys(props).forEach(function (k) { merged[k] = props[k]; });
          chain = chain.then(function () {
            return fetch(rootUrl() + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: merged })
            });
          });
        });
        return chain;
      })
      .catch(function (e) { log('retro-fit failed', e); });
  }

  /* -------------------------------------------------------- fetch fallback */
  /* Belt and braces: if any part of the theme builds a /cart/add.js body by
     hand instead of serializing the form, fold the properties in anyway. */

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        if (TAG_LINE_ITEMS && /\/cart\/add(\.js)?(\?|$)/.test(url) && init && init.body) {
          var d = load();
          if (d) init.body = decorateBody(init.body, lineItemProperties(d));
        }
      } catch (e) { log('interceptor skipped', e); }
      return nativeFetch.apply(this, arguments);
    };
  }

  function decorateBody(body, props) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      Object.keys(props).forEach(function (k) {
        if (!body.has('properties[' + k + ']')) body.append('properties[' + k + ']', props[k]);
      });
      return body;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      Object.keys(props).forEach(function (k) {
        if (!body.has('properties[' + k + ']')) body.append('properties[' + k + ']', props[k]);
      });
      return body;
    }
    if (typeof body === 'string') {
      try {
        var json = JSON.parse(body);
        var apply = function (obj) {
          obj.properties = obj.properties || {};
          Object.keys(props).forEach(function (k) {
            if (!(k in obj.properties)) obj.properties[k] = props[k];
          });
        };
        if (Array.isArray(json.items)) json.items.forEach(apply); else apply(json);
        return JSON.stringify(json);
      } catch (e) { /* not JSON — leave it alone */ }
    }
    return body;
  }

  /* ------------------------------------------------------------ public API */

  function commit(patch, source) {
    var d = load() || {};

    /* A different model year means a different vehicle, so the stored record
       starts over rather than blending the two. Without this, a VIN decode
       followed by a YMM search for something else leaves _Vehicle describing
       one truck and _Vehicle Year describing another. */
    if (d.year && patch.year && str(d.year) !== str(patch.year)) {
      log('year changed', d.year, '->', patch.year, '- resetting');
      d = {};
    }

    /* The VIN decode resolves to a variant (ARC vs ARC-R); the YMM finder only
       ever reaches the parent product. So a later YMM pick on the same vehicle
       must not overwrite the more precise VIN recommendation. */
    var keepRec = (source === 'YMM' && d.vin && d.recommends);

    Object.keys(patch).forEach(function (k) {
      if (keepRec && k === 'recommends') return;
      if (patch[k] !== undefined && patch[k] !== null && patch[k] !== '') d[k] = patch[k];
    });
    // A VIN decode after a YMM pick on the SAME vehicle records both.
    d.source  = (d.source && d.source.indexOf(source) === -1) ? 'VIN+YMM' : source;
    d.vehicle = vehicleString(d);
    d.ts      = Date.now();
    save(d);
    injectInputs();
    syncCartAttributes();
    log('captured', d);
    return d;
  }

  var SSFitment = {
    /* Called from shocksims-ymm-finder. year/make/model arrive as separate
       strings, and the finder's year list is already expanded from the variant
       ranges, so this is the exact model year the variant cannot express. */
    setYMM: function (v) {
      v = v || {};
      return commit({
        year:   v.year,
        make:   v.make,
        model:  v.model,
        trim:   v.trim,
        engine: v.engine,
        recommends: productName(v.recommends)
      }, 'YMM');
    },

    /* Called from shocksims-widget. `vehicle` is the decoded title string
       ("2022 GMC Sierra 1500 Denali"); make and model are not split out. */
    setVIN: function (vin, v) {
      v = v || {};
      vin = str(vin).toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
      if (vin.length !== 17) { log('rejected VIN (not 17 chars):', vin); return null; }
      return commit({
        vin: vin,
        vehicleRaw: v.vehicle,
        year:   v.year,
        trim:   v.trim,
        engine: v.engine,
        rpo:    v.rpo,
        recommends: productName(v.recommends),
        outcome: OUTCOMES[v.outcome] || v.outcome
      }, 'VIN');
    },

    get: load,
    clear: clear,
    refresh: function () { injectInputs(); syncCartAttributes(); },
    audit: audit,
    applyToExistingCart: applyToExistingCart,
    properties: function () { var d = load(); return d ? lineItemProperties(d) : null; }
  };

  window.SSFitment = SSFitment;

  function boot() { injectInputs(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); watchForms(); });
  } else {
    boot(); watchForms();
  }
  // Shopify theme editor re-renders sections without a page reload.
  document.addEventListener('shopify:section:load', boot);

})(window, document);